import React, { useEffect, useState } from 'react';

import '../../../theme/publicSurvey.css';

import FormSkeleton from '../../../app/components/FormSkeleton';
import {
  CreateUpdateSurveyAnswerDto,
  GetSurveyForClientDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useLocalTranslation } from '../../../common/hooks/useLocalTranslation';
import { LanguageType } from '../../../common/pokCore/validation/enums';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';

import { PublicSurveyContainer } from './PublicSurveyContainer';
import { PublicSurveyError } from './PublicSurveyError';
import { LanguageSwitcher } from './LanguageSwitcher';

interface SurveyViewSharedProps {
  identifier: string | undefined;
  submitError?: string;
  fetchSurvey: (identifier: string) => Promise<GetSurveyForClientDto>;
  onComplete?: (
    answers: CreateUpdateSurveyAnswerDto[],
    surveyId: string,
  ) => Promise<boolean>;
  isPreview?: boolean;
}

export const SurveyViewShared: React.FC<SurveyViewSharedProps> = ({
  identifier,
  submitError,
  fetchSurvey,
  onComplete,
  isPreview = false,
}) => {
  const [survey, setSurvey] = useState<GetSurveyForClientDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(submitError || null);
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [currentLanguage, setCurrentLanguage] = useState(
    LanguageType.PL as string,
  );

  const { t, tk } = useLocalTranslation('administrationLng', currentLanguage);

  const handleLanguageChange = (language: string) => {
    setCurrentLanguage(language);
  };

  useEffect(() => {
    if (!identifier) {
      setError(t(tk.publicSurvey.invalidLink));
      setIsLoading(false);
      return;
    }

    fetchSurvey(identifier)
      .then(data => {
        setSurvey(data);
        setLogo(data.company?.logo || undefined);
        setCurrentLanguage(data.language || LanguageType.PL);
        setIsLoading(false);
      })
      .catch(async error => {
        const errors = await responseErrors(error as ResponseError);
        setError(errors.join(', '));
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);

  if (isLoading) {
    return (
      <div
        className={`public-survey-hero-bg ${isPreview ? 'public-survey-hero-bg--preview' : ''} d-flex align-items-center justify-content-center p-4`}
      >
        <div className="public-survey-landing-wrapper">
          <div className="public-survey-landing-card public-survey-glass public-survey-shadow-lg">
            <FormSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <PublicSurveyError message={error} isPreview={isPreview} />;
  }

  if (!survey) {
    return null;
  }

  return (
    <>
      <LanguageSwitcher
        currentLanguage={currentLanguage}
        isPreview={isPreview}
        onLanguageChange={handleLanguageChange}
      />
      <PublicSurveyContainer
        survey={survey}
        logo={logo}
        onCompleted={onComplete}
        currentLanguage={currentLanguage}
        isPreview={isPreview}
      />
    </>
  );
};
